import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { LiveDot } from '@/components/generals/live-dot'
import { managedFlowSteps } from '@/constants/product'

interface FlowStepsPanelProps {
    className?: string
}

/** Miniature managed flow: each step ticks to done, one at a time, then the run starts over. */
export const FlowStepsPanel: FC<FlowStepsPanelProps> = ({ className }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref, { threshold: 0.3 })
    const reduced = useReducedMotion()
    const [done, setDone] = useState(0)
    const total = managedFlowSteps.length

    useEffect(() => {
        if (reduced) {
            setDone(total)
            return
        }

        if (!inView) return

        const delay = done >= total ? 2800 : done === 0 ? 800 : 1150
        const timer = window.setTimeout(() => {
            setDone(count => count >= total ? 0 : count + 1)
        }, delay)

        return () => window.clearTimeout(timer)
    }, [done, inView, reduced, total])

    const complete = done >= total

    return (
        <div ref={ref} className={cn('rounded-[22px] bg-cream p-2', className)}>
            <div className='flex items-center justify-between gap-3 px-3 pb-2 pt-2'>
                <span className='eyebrow min-w-0 truncate text-stone'>
                    Bookings to ERP
                    <span className='hidden sm:inline'>
                        {' · Run 2,184'}
                    </span>
                </span>

                <span className='eyebrow flex shrink-0 items-center gap-2 text-ink-soft'>
                    <LiveDot tone={complete ? 'signal' : 'coral'} pulse={!complete} />
                    {complete ? 'Complete' : 'Running'}
                </span>
            </div>

            <ol className='space-y-1'>
                {managedFlowSteps.map((step, index) => {
                    const isDone = index < done
                    const isRunning = index === done && !complete

                    return (
                        <li
                            key={step.label}
                            className={cn(
                                'flex items-center gap-3 rounded-2xl px-3 py-2.5 transition-colors duration-500',
                                isRunning ? 'bg-paper shadow-[0_6px_16px_-10px_rgba(22,20,15,0.3)]' : 'bg-transparent'
                            )}
                        >
                            <span className='relative grid size-6 shrink-0 place-items-center'>
                                {isDone && (
                                    <span className='grid size-6 animate-chip-pop place-items-center rounded-full bg-ultramarine text-white'>
                                        <Check aria-hidden='true' className='size-3.5' strokeWidth={2.6} />
                                    </span>
                                )}

                                {isRunning && (
                                    <span className='size-5 animate-spin rounded-full border-2 border-ultramarine/20 border-t-ultramarine' />
                                )}

                                {!isDone && !isRunning && (
                                    <span className='size-5 rounded-full border border-dashed border-ink/25' />
                                )}
                            </span>

                            <span className={cn('min-w-0 flex-1 truncate text-[14px] transition-colors duration-500', isDone || isRunning ? 'text-ink' : 'text-stone')}>
                                {step.label}
                            </span>

                            <span className='eyebrow hidden shrink-0 text-stone sm:inline'>
                                {step.system}
                            </span>

                            <span className={cn('eyebrow w-9 shrink-0 text-right transition-opacity duration-500', isDone ? 'text-ink-soft opacity-100' : 'opacity-0')}>
                                {step.time}
                            </span>
                        </li>
                    )
                })}
            </ol>

            <div className='mx-3 mb-2 mt-3 h-1 overflow-hidden rounded-full bg-ink/[0.06]'>
                <div
                    className='h-full rounded-full bg-ultramarine transition-[width] duration-700 ease-calm'
                    style={{ width: `${(Math.min(done, total) / total) * 100}%` }}
                />
            </div>
        </div>
    )
}
